import { useEffect, useState } from "react";
import { api } from "./api";

// Company branding — public subset of business_info served by /public/branding.
//
// Feb 2026 — added a 60-second TTL to the module cache. Before, branding was
// fetched once on page load and cached forever. That meant when an admin
// switched `gps_provider` in Company Branding, the driver's already-open
// tablet had no way to know until the next hard refresh — silently making
// "Driver phone GPS" mode look broken. With a 60s TTL, at most one minute
// of staleness after any config change (usually zero for a driver who's
// navigating between pages). Every branding change on the admin side calls
// `invalidateBranding()` explicitly too, so admins see instant feedback
// on their own screen.
const BRANDING_TTL_MS = 60_000;

let _brandingPromise = null;
let _brandingValue = null;
let _brandingLoadedAt = 0;

export function fetchBranding({ force = false } = {}) {
  const stale = Date.now() - _brandingLoadedAt > BRANDING_TTL_MS;
  if (force || stale) {
    _brandingPromise = null;
  }
  if (!_brandingPromise) {
    _brandingPromise = api
      .get("/public/branding")
      .then((r) => {
        _brandingValue = r.data;
        _brandingLoadedAt = Date.now();
        rememberTenantLocale(r.data);
        // Keep the browser tab in step with whichever tenant is signed in —
        // otherwise the title stays on whatever the unauthenticated login
        // page resolved to.
        if (r.data?.app_title) document.title = r.data.app_title;
        return r.data;
      })
      .catch(() => {
        _brandingPromise = null;
        return null;
      });
  }
  return _brandingPromise;
}

// Non-reactive getters for use outside hooks (toasts, print HTML, helpers).
// Feb 2026 — SaaS hardening: falls back through salvage_entity_* → trading_name
// → generic phrase. Any tenant with at least a trading_name gets a real
// company name in dialogs instead of the generic phrase, even if they never
// filled out the Company Branding form.
export const salvageShort = () =>
  _brandingValue?.salvage_entity_name
  || _brandingValue?.trading_name
  || "the towing company";
// Nav/labels want something short enough to sit in a menu row, so they fall
// back to the generic word rather than a sentence fragment.
// The tenant's currency code (NZ or AU at present) — several dialogs had
// "NZD" baked into their labels and validation toasts.
export const tenantCurrency = () => _brandingValue?.currency || "NZD";
// Example mobile number in the tenant's own country — an AU operator was
// being shown a +64 number to copy.
export const phonePlaceholder = () =>
  (_brandingValue?.country === "AU" ? "+61 4 1234 5678" : "+64 21 123 4567");
export const salvageLabel = () =>
  _brandingValue?.salvage_entity_name || "Salvage";
export const salvageFull = () =>
  _brandingValue?.salvage_entity_full
  || _brandingValue?.trading_name
  || "the towing company";

// Tenant timezone + locale — every date shown or entered in the app uses the
// company's timezone (Settings → Business Letterhead), not the device's.
// Last-known values persist in localStorage so offline tablets stay correct.
const DEFAULT_TZ = "Pacific/Auckland";
const DEFAULT_LOCALE = "en-NZ";
const TZ_KEY = "tenant_tz";
const LOCALE_KEY = "tenant_locale";
let _tz = localStorage.getItem(TZ_KEY) || DEFAULT_TZ;
let _locale = localStorage.getItem(LOCALE_KEY) || DEFAULT_LOCALE;

function rememberTenantLocale(b) {
  if (b?.timezone) { _tz = b.timezone; localStorage.setItem(TZ_KEY, _tz); }
  if (b?.number_locale) { _locale = b.number_locale; localStorage.setItem(LOCALE_KEY, _locale); }
}

export const tenantTz = () => _tz;
export const tenantLocale = () => _locale;

export function invalidateBranding() {
  _brandingPromise = null;
  _brandingValue = null;
  _brandingLoadedAt = 0;
}

export function useBranding() {
  const [branding, setBranding] = useState(_brandingValue);
  useEffect(() => {
    let on = true;
    // First render: use whatever's already cached (instant paint), then
    // trigger a fetch that respects the TTL. On subsequent renders after
    // the TTL expires, this will pick up config changes automatically.
    fetchBranding().then((b) => on && setBranding(b));
    return () => {
      on = false;
    };
  }, []);
  return branding;
}
